/**
 * ドライバー UI 向け最小オフラインキュー.
 *
 * オンライン中は通常通り実行し、ネットワークエラーまたは一時的 HTTP エラー時だけ
 * キューへ退避。以降 `flushOfflineQueue` で順次再試行する。
 *
 * localStorage スキーマ: `driver.offline-queue.v1` -> `OfflineAction[]`
 */

export type OfflineActionKind =
  | "update-stop-status"
  // 巻き戻し: completed → in_transit。サーバは completed 以外なら not_revertable を返すため
  // 再送は実質冪等 (二重適用されない)。update-stop-status とは path/操作が異なるため別種別。
  | "revert-stop-status"
  // 打刻修正: actual_departure 等を UPDATE で上書き。再送しても結果は同じで冪等。
  | "timestamp-edit"
  // 遅延報告: 初回送信時の request_id を body に保持し、サーバ側で同一操作の再送を冪等化する。
  | "delay-report"
  | "clock-in"
  | "clock-out"
  | "create-expense"
  | "submit-daily-report"
  | "create-vehicle-inspection"
  | "create-alcohol-check"
  | "create-refueling";

export interface OfflineAction {
  id: string;
  kind: OfflineActionKind;
  /** POST/PUT target path (apiBaseUrl 除く絶対パス) */
  path: string;
  method: "POST" | "PUT";
  /** JSON body */
  body: unknown;
  /** 発生時刻 (ISO) */
  enqueuedAt: string;
  /** リトライ回数 */
  attempts: number;
  /** 最後のエラー */
  lastError?: string;
}

const STORAGE_KEY = "driver.offline-queue.v1";
const MAX_ATTEMPTS = 5;

/** flush が送信を試みず破棄する配送実行系 (アルコール安全ゲート対象)。UI の再試行可否判定でも使う。 */
export const DELIVERY_EXECUTION_KINDS: readonly OfflineActionKind[] = [
  "update-stop-status",
  "revert-stop-status",
  "timestamp-edit",
  "delay-report",
];

/** dead-letter 一覧やバナーで使う日本語ラベル。 */
export const OFFLINE_ACTION_LABELS: Record<OfflineActionKind, string> = {
  "update-stop-status": "配送ステータス打刻",
  "revert-stop-status": "ステータス巻き戻し",
  "timestamp-edit": "打刻修正",
  "delay-report": "遅延報告",
  "clock-in": "出勤打刻",
  "clock-out": "退勤打刻",
  "create-expense": "経費登録",
  "submit-daily-report": "日報提出",
  "create-vehicle-inspection": "車両点検",
  "create-alcohol-check": "アルコール検査",
  "create-refueling": "給油記録",
};

// ── dead-letter (Issue #252) ──────────────────────────────
// flush が破棄したアクションの退避先。ドライバーが内容確認・再試行・明示破棄できるよう
// localStorage に保持する。保持期間 30 日・上限 50 件 (古い順に押し出し) は docs/05_ui/06_driver_pwa.md の決定事項。

export type DeadLetterReason = "http_4xx" | "max_attempts" | "delivery_blocked";

export interface DeadLetterEntry {
  /** 破棄されたアクション本体 (body / enqueuedAt / id を原型のまま保持)。 */
  action: OfflineAction;
  reason: DeadLetterReason;
  /** reason=http_4xx のときの HTTP ステータス。 */
  httpStatus?: number;
  /** サーバ応答の message (取得できた場合のみ)。ドライバーへの理由表示に使う。 */
  serverMessage?: string;
  /** 破棄時刻 (ISO)。保持期間判定に使う。 */
  droppedAt: string;
}

const DEAD_LETTER_KEY = "driver.offline-queue.dead-letter.v1";
const DEAD_LETTER_MAX = 50;
const DEAD_LETTER_RETENTION_MS = 30 * 24 * 3600 * 1000;

/** 保持期間 (30日) 超過を除去し、上限 (50件) を超えた分は古い順に押し出す。 */
function pruneDeadLetters(entries: DeadLetterEntry[]): DeadLetterEntry[] {
  const cutoff = Date.now() - DEAD_LETTER_RETENTION_MS;
  const kept = entries.filter((e) => {
    const t = Date.parse(e.droppedAt);
    // droppedAt が壊れているエントリは監査価値を優先して保持する (削除は明示破棄に委ねる)
    return Number.isNaN(t) || t >= cutoff;
  });
  return kept.slice(-DEAD_LETTER_MAX);
}

export function readDeadLetters(): DeadLetterEntry[] {
  const s = safeStorage();
  if (!s) return [];
  const raw = s.getItem(DEAD_LETTER_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return pruneDeadLetters(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
}

export function writeDeadLetters(entries: DeadLetterEntry[]): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(DEAD_LETTER_KEY, JSON.stringify(pruneDeadLetters(entries)));
  } catch {
    // localStorage 容量超過等は握りつぶす
  }
}

export function deadLetterSize(): number {
  return readDeadLetters().length;
}

/**
 * dead-letter のエントリをメインキューへ戻す (attempts はリセット)。
 * body / enqueuedAt / id は原型を維持するため、#236 の idempotency_key / occurred_at も
 * 初回操作時の値のまま再送される。
 */
export function retryDeadLetter(actionId: string): boolean {
  const entries = readDeadLetters();
  const entry = entries.find((e) => e.action.id === actionId);
  if (!entry) return false;
  writeDeadLetters(entries.filter((e) => e.action.id !== actionId));
  const queue = readQueue();
  queue.push({ ...entry.action, attempts: 0, lastError: undefined });
  writeQueue(queue);
  return true;
}

/** dead-letter のエントリを明示的に破棄する。 */
export function discardDeadLetter(actionId: string): boolean {
  const entries = readDeadLetters();
  if (!entries.some((e) => e.action.id === actionId)) return false;
  writeDeadLetters(entries.filter((e) => e.action.id !== actionId));
  return true;
}

export function clearDeadLetters(): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.removeItem(DEAD_LETTER_KEY);
  } catch {
    // noop
  }
}

/** ドライバー向けの破棄理由テキスト。 */
export function deadLetterReasonText(entry: DeadLetterEntry): string {
  switch (entry.reason) {
    case "delivery_blocked":
      return "アルコール検査がPASSになるまで送信できません";
    case "max_attempts":
      return "再試行の上限回数に達しました";
    case "http_4xx":
      return entry.serverMessage ?? `サーバに拒否されました (HTTP ${entry.httpStatus ?? "4xx"})`;
  }
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readQueue(): OfflineAction[] {
  const s = safeStorage();
  if (!s) return [];
  const raw = s.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeQueue(queue: OfflineAction[]): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage 容量超過等は握りつぶす
  }
}

export function enqueueAction(
  action: Omit<OfflineAction, "id" | "enqueuedAt" | "attempts">,
): OfflineAction {
  const record: OfflineAction = {
    ...action,
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    enqueuedAt: new Date().toISOString(),
    attempts: 0,
  };
  const queue = readQueue();
  queue.push(record);
  writeQueue(queue);
  return record;
}

export function queueSize(): number {
  return readQueue().length;
}

export interface FlushResult {
  processed: number;
  failed: number;
  dropped: number;
  /**
   * この flush で dead-letter へ退避したエントリ。UI の破棄通知に使う。
   * 409 (冪等重複=処理済) は dropped に数えるが退避しないため、dropped と件数が一致しないことがある。
   */
  droppedEntries: DeadLetterEntry[];
}

/** 4xx 応答の JSON body から message を best-effort で取り出す (無ければ undefined)。 */
async function extractServerMessage(res: Response): Promise<string | undefined> {
  try {
    const data: unknown = await res.json();
    if (data && typeof data === "object" && "message" in data) {
      const message = (data as { message: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
  } catch {
    // body なし / JSON 以外
  }
  return undefined;
}

// flush の直列化。mount 時の effect 再実行・online イベント・手動再送・dead-letter 再試行が
// 重なると、同じキューを二重に読んで二重送信 (非冪等な遅延報告の二重登録) や dead-letter の
// 二重退避が起きるため、実行中は後発を待たせて順番に流す。
// スキップ方式にしないこと: 再試行直後の flush が先行 flush と重なると取り零され、
// キューに残ったまま送信されない (次の online 復帰まで滞留する)。
let flushChain: Promise<unknown> = Promise.resolve();

export function flushOfflineQueue(params: {
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
  /** false の間は配送実行系を送信せず破棄する。 */
  deliveryExecutionAllowed?: boolean;
}): Promise<FlushResult> {
  const result = flushChain.then(() => flushOfflineQueueInner(params));
  // 失敗しても後続の flush を止めない
  flushChain = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

async function flushOfflineQueueInner(params: {
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
  deliveryExecutionAllowed?: boolean;
}): Promise<FlushResult> {
  const queue = readQueue();
  if (queue.length === 0) return { processed: 0, failed: 0, dropped: 0, droppedEntries: [] };

  const fetchFn = params.fetchImpl ?? fetch;
  const remaining: OfflineAction[] = [];
  const droppedEntries: DeadLetterEntry[] = [];
  let processed = 0;
  let failed = 0;
  let dropped = 0;

  const dropToDeadLetter = (
    action: OfflineAction,
    reason: DeadLetterReason,
    detail?: { httpStatus?: number; serverMessage?: string },
  ) => {
    dropped += 1;
    droppedEntries.push({
      action,
      reason,
      ...(detail?.httpStatus !== undefined ? { httpStatus: detail.httpStatus } : {}),
      ...(detail?.serverMessage !== undefined ? { serverMessage: detail.serverMessage } : {}),
      droppedAt: new Date().toISOString(),
    });
  };

  for (const action of queue) {
    if (
      params.deliveryExecutionAllowed === false &&
      DELIVERY_EXECUTION_KINDS.includes(action.kind)
    ) {
      dropToDeadLetter(action, "delivery_blocked");
      continue;
    }
    try {
      const res = await fetchFn(`${params.apiBaseUrl.replace(/\/$/, "")}${action.path}`, {
        method: action.method,
        headers: { "Content-Type": "application/json" },
        // httpOnly Cookie セッションを送る。Authorization: Bearer は使わない。
        credentials: "include",
        body: JSON.stringify(action.body),
      });
      if (res.ok) {
        processed += 1;
        continue;
      }
      // 4xx (ただし 408/429 を除く) は再試行しても成功しないので drop
      if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
        // 409 は冪等重複 (サーバに処理済) なので失敗として退避しない
        if (res.status === 409) {
          dropped += 1;
          continue;
        }
        dropToDeadLetter(action, "http_4xx", {
          httpStatus: res.status,
          serverMessage: await extractServerMessage(res),
        });
        continue;
      }
      throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      const attempts = action.attempts + 1;
      const lastError = e instanceof Error ? e.message : String(e);
      if (attempts >= MAX_ATTEMPTS) {
        dropToDeadLetter({ ...action, attempts, lastError }, "max_attempts");
        continue;
      }
      remaining.push({ ...action, attempts, lastError });
      failed += 1;
    }
  }

  writeQueue(remaining);
  if (droppedEntries.length > 0) {
    writeDeadLetters([...readDeadLetters(), ...droppedEntries]);
  }
  return { processed, failed, dropped, droppedEntries };
}

export function clearOfflineQueue(): void {
  writeQueue([]);
}
